'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, Goal, H, Hl, Lesson, M, MB, NumberQuiz, P, Quiz, Step } from '../_components/lesson'
import { DotStrip } from '../_components/widgets/LinearWidgets'
import * as EX from './exercises/linear-algebra'

const DotProductViz = dynamic(() => import('../_components/widgets/LinearWidgets').then(m => m.DotProductViz), { ssr: false })

export default function DotProduct() {
  return (
    <Lesson>
      <Step gate="nq-dot">
        <H>Multiply, then add</H>
        <P>
          The <strong>dot product</strong> takes two vectors of the same length and gives back a single number: multiply the
          matching entries, then add everything up.
        </P>
        <MB>{'\\blue{\\mathbf{a}} \\cdot \\amber{\\mathbf{b}} = \\sum_{i=1}^{n} \\blue{a_i}\\,\\amber{b_i} = \\blue{a_1}\\amber{b_1} + \\blue{a_2}\\amber{b_2} + \\dots + \\blue{a_n}\\amber{b_n}'}</MB>
        <Figure n="3.4" caption="Pair up, multiply, add.">
          <DotStrip a={[2, 1, 3]} b={[0.5, 2.2, 1.1]} />
        </Figure>
        <P>
          You use this all the time without the name. If you buy 2 apples at 0.50, 1 loaf at 2.20 and 3 milks at 1.10, your bill
          is the dot product of quantities and prices.
        </P>
        <Exercise id="shopping-dot" title="A shopping bill" {...EX.shoppingDot} />
        <NumberQuiz id="nq-dot" question={<>What is <M>{'[1, 2, 3] \\cdot [4, 5, 6]'}</M>?</>} answer={32} explain={<>1·4 + 2·5 + 3·6 = 4 + 10 + 18 = 32.</>} />
      </Step>

      <Step gate="goal-perp">
        <H>A measure of agreement</H>
        <P>Here’s what makes the dot product the most important operation in AI. Geometrically, it tells you how much two arrows point the same way:</P>
        <ul className="gd-list">
          <li>
            <Hl c="green">Positive</Hl> — they point roughly the same direction.
          </li>
          <li>
            <strong>Zero</strong> — they’re at a right angle (perpendicular): completely unrelated.
          </li>
          <li>
            <Hl c="pink">Negative</Hl> — they point in opposite-ish directions.
          </li>
        </ul>
        <Goal id="goal-perp" task={<>Drag the arrows until the dot product is zero — make them perpendicular.</>}>
          {onReach => <DotProductViz onPerpendicular={onReach} />}
        </Goal>
        <P>
          The shaded segment along <M>{'\\blue{\\mathbf{a}}'}</M> is the <strong>projection</strong> of <M>{'\\amber{\\mathbf{b}}'}</M>
          : its “shadow” on a. The dot product is that shadow’s length times a’s length. When b is perpendicular, it casts no
          shadow — dot product 0.
        </P>
      </Step>

      <Step gate="q-cos">
        <H>Cosine similarity</H>
        <P>
          A long arrow gives a bigger dot product than a short one, even pointing the same way. To compare <em>only</em>{' '}
          direction, divide by both lengths:
        </P>
        <MB>{'\\violet{\\cos\\theta} = \\frac{\\mathbf{a} \\cdot \\mathbf{b}}{\\lVert \\mathbf{a} \\rVert\\, \\lVert \\mathbf{b} \\rVert}'}</MB>
        <P>
          This is <strong>cosine similarity</strong>: always between −1 (opposite) and 1 (identical direction). It’s how search
          engines compare documents, how recommendation systems find “people like you”, and how AI finds words with similar
          meanings.
        </P>
        <Exercise id="similar-words" title="Which word is like “cat”?" {...EX.similarWords} />
        <Quiz
          id="q-cos"
          question="Two word vectors have cosine similarity 0.98. What can you conclude?"
          options={[
            { label: 'The words are probably closely related in meaning', correct: true },
            { label: 'The vectors have the same length', why: 'Cosine similarity ignores length entirely — it only compares direction.' },
            { label: 'The words are opposites', why: 'Opposites would be near −1.' },
          ]}
          explain="Near 1 means the arrows point the same way. If the vectors were learned well, that means similar meaning."
        />
      </Step>

      <Step gate="dot-cosine">
        <H>Write them</H>
        <Exercise
          id="dot-cosine"
          title="dot and cosineSimilarity"
          {...EX.dotCosine}
          hints={[
            <>A loop over the indices, adding <C>a[i] * b[i]</C> each time.</>,
            <>
              <C>return dot(a, b) / (vec.norm(a) * vec.norm(b))</C>
            </>,
          ]}
        />
      </Step>

      <Step gate="q-neuron">
        <H>Every neuron is a dot product</H>
        <P>Remember the line model, <M>{'\\hat{y} = wx + b'}</M>? With several inputs, it becomes:</P>
        <MB>{'\\amber{\\hat{y}} = \\green{w_1}\\blue{x_1} + \\green{w_2}\\blue{x_2} + \\dots + \\green{w_n}\\blue{x_n} + \\green{b} = \\green{\\mathbf{w}} \\cdot \\blue{\\mathbf{x}} + \\green{b}'}</MB>
        <P>
          A dot product between the <Hl c="green">weights</Hl> and the <Hl c="blue">inputs</Hl>. That’s exactly what one
          artificial neuron computes. Big positive weight: “this input matters, and more is better”. Negative weight: “more of
          this is worse”. The famous <em>attention</em> mechanism in transformers? Dot products between words, deciding which
          words should listen to which.
        </P>
        <Quiz
          id="q-neuron"
          question={
            <>
              A house-price model has weights <M>{'\\mathbf{w} = [2000, 5000]'}</M> for <M>{'[\\text{size}, \\text{rooms}]'}</M>{' '}
              and bias 10000. What does it predict for a 100 m², 3-room house?
            </>
          }
          options={[
            { label: '225,000', correct: true },
            { label: '215,000', why: 'Don’t forget to add the bias of 10,000.' },
            { label: '7,000', why: 'Multiply each weight by its input first: 2000 × 100 and 5000 × 3.' },
          ]}
          explain="2000·100 + 5000·3 + 10000 = 200,000 + 15,000 + 10,000 = 225,000. A dot product plus a bias."
        />
        <Callout kind="why">
          Dot products are cheap: just multiplies and adds. Chips are built to do trillions of them per second — which is why
          modern AI is possible at all.
        </Callout>
      </Step>
    </Lesson>
  )
}
